import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { watch } from 'vue';
import { useStore } from 'vuex';
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageEmpty } from '@/plugins/ws';

import {RootState} from "@/store";

type CurrencyWithId = {
  idCurrency: number;
};

// Базовый тип для create/update операций валют
type CurrencyCreateUpdateData = CurrencyWithId & {
  code: string;
  name: string;
  description: string;
};

// Типы для WebSocket сообщений компонента currencies
type CurrencyRootMessage = WebSocketMessageWithFields<'pair' | 'pair-blockchains' | 'currency-root' | 'currency-blockchain', {
  currency: unknown;
  currencyBlockchains: unknown[];
}>;
type CurrencyRootNotFoundMessage = WebSocketMessageEmpty<'currency-root-not-found' | 'currency-blockchain-not-found'>;
type CurrencyListMessage = WebSocketMessageWithFields<'global-pair-list' | 'global-pair-root' | 'global-pair-exchange' | 'global-pair-indicator' | 'global-pair-calc' | 'global-pair-data' | 'global-pair-graph' | 'global-currency-list' | 'exchange-currency' | 'exchange-pair' | 'currency-list' | 'main' | 'pairs' | 'pairs-quantfury' | 'calc-info' | 'helper-agents', {
  currencies: unknown[];
}>;
type CurrencyCreateMessage = WebSocketMessageWithFields<'currency-create' | 'currency-create-success', CurrencyCreateUpdateData>;
type CurrencyUpdateMessage = WebSocketMessageWithFields<'currency-update' | 'currency-update-success', CurrencyCreateUpdateData>;

type CurrencyDeleteMessage = WebSocketMessageWithFields<'currency-delete' | 'currency-delete-success', CurrencyWithId>;
type CurrencyLockMessage = WebSocketMessageWithFields<'currency-lock' | 'currency-lock-success', CurrencyWithId>;
type CurrencyUnlockMessage = WebSocketMessageWithFields<'currency-unlock' | 'currency-unlock-success', CurrencyWithId>;

type CurrencyBlockchainCreateMessage = WebSocketMessageWithFields<'currency-blockchain-create' | 'currency-blockchain-create-success', {
  idCurrency: number;
  idBlockchain: number;
  contract: string;
  main: boolean;
}>;

type CurrencyBlockchainUpdateMessage = WebSocketMessageWithFields<'currency-blockchain-update' | 'currency-blockchain-update-success', {
  idCurrency: number;
  idBlockchain: number;
  contract: string;
  main: boolean;
}>;

type CurrencyBlockchainDeleteMessage = WebSocketMessageWithFields<'currency-blockchain-delete' | 'currency-blockchain-delete-success', {
  idCurrency: number;
  idBlockchain: number;
}>;

type CurrencyBlockchainLockMessage = WebSocketMessageWithFields<'currency-blockchain-lock' | 'currency-blockchain-lock-success', {
  idCurrency: number;
  idBlockchain: number;
}>;

type CurrencyBlockchainUnlockMessage = WebSocketMessageWithFields<'currency-blockchain-unlock' | 'currency-blockchain-unlock-success', {
  idCurrency: number;
  idBlockchain: number;
}>;

// Объединенный тип для всех сообщений
type CurrenciesWebSocketMessage = 
  | CurrencyRootMessage 
  | CurrencyRootNotFoundMessage 
  | CurrencyListMessage 
  | CurrencyCreateMessage 
  | CurrencyUpdateMessage 
  | CurrencyDeleteMessage 
  | CurrencyLockMessage 
  | CurrencyUnlockMessage 
  | CurrencyBlockchainCreateMessage 
  | CurrencyBlockchainUpdateMessage 
  | CurrencyBlockchainDeleteMessage 
  | CurrencyBlockchainLockMessage 
  | CurrencyBlockchainUnlockMessage;

// WebSocket message handler

export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'dataCurrencies',
},
  __name: 'currencies',
  setup(__props) {

const store = useStore<RootState>();



// Базовый тип для сообщений с ID валюты
const handleWebSocketMessage = (data: CurrenciesWebSocketMessage): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'pair':
    case 'pair-blockchains':
    case 'currency-root':
    case 'currency-blockchain':
      store.dispatch('currency/wsSetCurrency', data.currency);
      store.dispatch('currency/wsSetCurrencyBlockchains', {
        currencyBlockchains: data.currencyBlockchains,
      });
      break;
    case 'currency-root-not-found':
    case 'currency-blockchain-not-found':
      store.dispatch('currency/wsSetEmptyCurrency');
      break;

    case 'global-pair-list':
    case 'global-pair-root':
    case 'global-pair-exchange':
    case 'global-pair-indicator':
    case 'global-pair-calc':
    case 'global-pair-data':
    case 'global-pair-graph':
    case 'global-currency-list':
    case 'exchange-currency':
    case 'exchange-pair':
    case 'currency-list':
    case 'main':
    case 'pairs':
    case 'pairs-quantfury':
    case 'calc-info':
    case 'helper-agents':
      store.dispatch('currencies/wsSetCurrencies', {
        currencies: data.currencies,
      });
      break;

    case 'currency-create':
    case 'currency-create-success':
      store.dispatch('currencies/wsCurrencyCreate', {
        idCurrency: data.idCurrency,
        code: data.code,
        name: data.name,
        description: data.description,
      });
      break;

    case 'currency-update':
    case 'currency-update-success':
      store.dispatch('currencies/wsCurrencyUpdate', {
        idCurrency: data.idCurrency,
        code: data.code,
        name: data.name,
        description: data.description,
      });
      store.dispatch('currency/wsCurrencyUpdate', {
        idCurrency: data.idCurrency,
        code: data.code,
        name: data.name,
        description: data.description,
      });
      break;

    case 'currency-delete':
    case 'currency-delete-success':
      store.dispatch('currencies/wsCurrencyDelete', {
        idCurrency: data.idCurrency,
      });
      store.dispatch('currency/wsCurrencyDelete', {
        idCurrency: data.idCurrency,
      });
      break;

    case 'currency-lock':
    case 'currency-lock-success':
      store.dispatch('currencies/wsCurrencyLock', {
        idCurrency: data.idCurrency,
      });
      store.dispatch('currency/wsCurrencyLock', {
        idCurrency: data.idCurrency,
      });
      break;

    case 'currency-unlock':
    case 'currency-unlock-success':
      store.dispatch('currencies/wsCurrencyUnlock', {
        idCurrency: data.idCurrency,
      });
      store.dispatch('currency/wsCurrencyUnlock', {
        idCurrency: data.idCurrency,
      });
      break;

    case 'currency-blockchain-create':
    case 'currency-blockchain-create-success':
      store.dispatch('currency/wsCurrencyBlockchainCreate', {
        idCurrency: data.idCurrency,
        idBlockchain: data.idBlockchain,
        contract: data.contract,
        main: data.main,
      });
      break;

    case 'currency-blockchain-update':
    case 'currency-blockchain-update-success':
      store.dispatch('currency/wsCurrencyBlockchainUpdate', {
        idCurrency: data.idCurrency,
        idBlockchain: data.idBlockchain,
        contract: data.contract,
        main: data.main,
      });
      break;

    case 'currency-blockchain-delete':
    case 'currency-blockchain-delete-success':
      store.dispatch('currency/wsCurrencyBlockchainDelete', {
        idCurrency: data.idCurrency,
        idBlockchain: data.idBlockchain,
      });
      break;

    case 'currency-blockchain-lock':
    case 'currency-blockchain-lock-success':
      store.dispatch('currency/wsCurrencyBlockchainLock', {
        idCurrency: data.idCurrency,
        idBlockchain: data.idBlockchain,
      });
      break;

    case 'currency-blockchain-unlock':
    case 'currency-blockchain-unlock-success':
      store.dispatch('currency/wsCurrencyBlockchainUnlock', {
        idCurrency: data.idCurrency,
        idBlockchain: data.idBlockchain,
      });
      break;
  }
};

watch(latestMessage, handleWebSocketMessage);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div"))
}
}

})